import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { Check } from '@untitled-ui/icons-react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

const checkboxSizes = {
  sm: 'size-4 rounded',
  md: 'size-5 rounded-md',
};

const iconSizes = {
  sm: 16,
  md: 20,
};

export type CheckboxProps = RadixCheckbox.CheckboxProps & {
  size?: 'sm' | 'md';
  className?: string;
};

export function Checkbox(props: CheckboxProps) {
  const { size = 'md', className = '', ...checkboxProps } = props;
  return (
    <RadixCheckbox.Root
      {...checkboxProps}
      className={twMerge(
        'shrink-0 border border-branding-gray-300 bg-white text-branding-accent-600 hover:border-branding-accent-600 hover:bg-branding-accent-50 focus:ring-4 focus:ring-branding-accent-200 data-[state=checked]:border-branding-accent-600 data-[state=checked]:bg-branding-accent-50 disabled:border-branding-gray disabled:bg-branding-gray-100 disabled:text-branding-gray',
        checkboxSizes[size],
        className
      )}
    >
      <RadixCheckbox.Indicator className="flex h-full w-full items-center justify-center">
        <Check width={iconSizes[size]} height={iconSizes[size]} />
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );
}

export type CheckboxFieldProps = {
  id?: string;
  size?: 'sm' | 'md';
  children?: React.ReactNode;
  className?: string;
  labelClassName?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
};

export function CheckboxField(props: CheckboxFieldProps) {
  const {
    id,
    children,
    className = '',
    size = 'md',
    labelClassName = '',
    disabled,
    checked,
    onCheckedChange,
    ...rest
  } = props;

  const checkboxId = React.useId();
  const inputId = id ?? checkboxId;

  return (
    <div
      className={twMerge(
        'inline-flex cursor-pointer items-center justify-center',
        children !== null && children !== undefined ? 'gap-2' : '',
        disabled === true ? 'cursor-not-allowed opacity-50' : '',
        labelClassName
      )}
    >
      <Checkbox
        id={inputId}
        size={size}
        className={className}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        {...rest}
      />
      {children === null || children === undefined ? null : (
        <label htmlFor={inputId} className="font-body-2-r cursor-pointer text-dark-gray">
          {children}
        </label>
      )}
    </div>
  );
}
